import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { X } from 'react-feather';
import type { ShiritoriGameStore } from '@/game/core/game.store';
import { LocalStorageKeyName, RemoveLocalStorage } from '@/util/local-storage';
import { getMatchStats } from '@/util/stats';
import { Button } from '../home/buttons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { domScreenshotShare } from '@/util/share-helper';
import { Card } from '../home/card';
import { useKeyboardShortcuts } from '@/util/use-keyboard-shortcuts';

interface EndGameModalProps {
    game: ShiritoriGameStore;
    onDismiss: () => void;
}

export const EndGameModal = observer(function EndGameModal({ game, onDismiss }: EndGameModalProps) {
    const [isSharing, setIsSharing] = useState(false);
    const [hideModalForShare, setHideModalForShare] = useState(false);
    const { t } = useTranslation();

    const shortcuts = useMemo(() => {
        return game.isGameOver ? [{ key: 'Escape', allowInInput: true, action: onDismiss }] : []
    }, [game.isGameOver, onDismiss]);

    useKeyboardShortcuts(shortcuts);

    if (!game.isGameOver) {
        return null;
    }

    const gameStats = getMatchStats(game.data);

    RemoveLocalStorage(LocalStorageKeyName.GameId);

    async function captureBoardAndShare() {
        const boardElement = document.getElementById('shiritori-board');

        if (boardElement) {
            try {
                setHideModalForShare(true);
                setIsSharing(true);

                await domScreenshotShare(boardElement, 'shiritori-board', {
                    backgroundColor: 'white',
                    fallbackURL: ({ width: w, height: h }) => `https://placehold.co/${w}x${h}`,
                    width: 500,
                    height: 500,
                    quality: 0.7
                });
            } catch (error) {
                console.error('Unable to share board', error);
            } finally {
                setHideModalForShare(false);
                setIsSharing(false);
            }
        }
    };

    const mainItems = [
        { display: t('game.gameplay.endGame.statsTotalTime'), value: gameStats.totalMatchTime },
        { display: t('game.gameplay.endGame.statsSquaresFilled'), value: `${gameStats.validPlays} / ${gameStats.board.cells}` },
        { display: t('game.gameplay.endGame.statsLongestWord'), value: gameStats.longestWord },
    ];

    const containerClass = `fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-sm px-4 ${hideModalForShare ? 'hidden' : ''}`;

    return (
        <div className={containerClass}>
            <Card header={t('game.gameplay.endGame.header')} role="dialog" aria-modal="true" className="relative w-full max-w-lg shadow-2xl">
                <button type="button" aria-keyshortcuts="Escape" aria-label={t('game.gameplay.endGame.ariaCloseLabel')} onClick={onDismiss} className="absolute right-3 top-3 rounded-full cursor-pointer p-2 hover:bg-primary transition">
                    <X className="w-5 h-5 opacity-70" />
                </button>

                <h3 className="text-center text-sm opacity-80 leading-relaxed"></h3>

                <div className="mt-2 max-h-48 overflow-y-auto">
                    {mainItems.map(({ display, value }) =>
                        <div key={display} className="flex items-center justify-between px-3 py-2 text-sm">
                            <div className="font-thin leading-tight">{display}</div>
                            <div className="text-right text-sm font-semibold">{value}</div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col justify-stretch mt-4 space-y-3">
                    <Button type="button" hotkey={{ key: '0', display: '0' }} onClick={captureBoardAndShare} isLoading={isSharing}>
                        {isSharing ? t('game.gameplay.endGame.sharing') : t('game.gameplay.endGame.share')}
                    </Button>
                    <Link to="/create">
                        <Button hotkey={{ key: '1', display: '1' }} className="w-full">{t('home.btnNewGame')}</Button>
                    </Link>
                    <Link to="/">
                        <Button hotkey={{ key: '2', display: '2' }} variant="secondary" className="w-full">{t('home.btnMainMenu')}</Button>
                    </Link>
                </div>
            </Card>
        </div>
    );
});
